import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';

export interface MessageNotification {
  id: string;
  conversationId: string;
  senderId: string;
  senderName: string;
  content: string;
  createdAt: string;
}

export type MessageHandler = (notification: MessageNotification) => void;

class WebSocketService {
  private client: Client | null = null;
  private messageHandlers: Set<MessageHandler> = new Set();
  private isConnected = false;
  private userId: string | null = null;

  connect(userId: string, accessToken: string) {
    console.log('🔗 Attempting WebSocket connection for user:', userId);
    
    // Always reconnect if token has changed or no connection exists
    if (this.client && this.isConnected && this.userId === userId) {
      console.log('🔄 Disconnecting existing connection');
      this.disconnect();
    }

    this.userId = userId;
    console.log('🚀 Starting new WebSocket connection...');

    // Construct WebSocket URL from API URL
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8081/api';
    const wsUrl = apiUrl.replace('/api', '/ws');

    this.client = new Client({
      webSocketFactory: () => new SockJS(wsUrl),
      connectHeaders: {
        Authorization: `Bearer ${accessToken}`
      },
      ...(process.env.NODE_ENV === 'development' && { debug: (str) => console.log('STOMP Debug:', str) }),
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
      onConnect: (frame) => {
        console.log('✅ WebSocket connected successfully');
        this.isConnected = true;
        this.subscribeToMessages();
      },
      onDisconnect: (frame) => {
        console.log('🔌 WebSocket disconnected');
        this.isConnected = false;
      },
      onStompError: (frame) => {
        console.error('❌ STOMP Error:', frame);
        this.isConnected = false;
      },
      onWebSocketError: (error) => {
        console.error('❌ WebSocket Error:', error);
        this.isConnected = false;
      }
    });

    this.client.activate();
  }

  private subscribeToMessages() {
    if (!this.client || !this.userId) {
      return;
    }
    
    // Subscribe to personal message queue
    this.client.subscribe(`/user/queue/messages`, (message) => {
      try {
        const notification: MessageNotification = JSON.parse(message.body);
        
        // Notify all registered handlers
        this.messageHandlers.forEach((handler) => {
          handler(notification);
        });
      } catch (error) {
        // Silent error handling in production
      }
    });
  }

  addMessageHandler(handler: MessageHandler) {
    this.messageHandlers.add(handler);
  }

  removeMessageHandler(handler: MessageHandler) {
    this.messageHandlers.delete(handler);
  }

  disconnect() {
    if (this.client) {
      this.client.deactivate();
      this.client = null;
    }
    this.isConnected = false;
    this.userId = null;
    this.messageHandlers.clear();
  }

  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      userId: this.userId
    };
  }
}

export const websocketService = new WebSocketService();